import { useMemo } from 'react'
import { BarChart3 } from 'lucide-react'

import { BranchComparisonReportPanel } from '@/components/reports/BranchComparisonReportPanel'
import { GatewayTransactionReportPanel } from '@/components/reports/GatewayTransactionReportPanel'
import { AppointmentReportPanel } from '@/components/reports/AppointmentReportPanel'
import { OnlineBookingFunnelReportPanel } from '@/components/reports/OnlineBookingFunnelReportPanel'
import { VatSummaryReportPanel } from '@/components/reports/VatSummaryReportPanel'
import { VoidDiscountAuditReportPanel } from '@/components/reports/VoidDiscountAuditReportPanel'
import { ZReportHistoricalReportPanel } from '@/components/reports/ZReportHistoricalReportPanel'
import { CommissionReportPanel } from '@/components/reports/CommissionReportPanel'
import { DailySalesReportPanel } from '@/components/reports/DailySalesReportPanel'
import { PaymentMixReportPanel } from '@/components/reports/PaymentMixReportPanel'
import { RetailSalesReportPanel } from '@/components/reports/RetailSalesReportPanel'
import { ServiceMixReportPanel } from '@/components/reports/ServiceMixReportPanel'
import { TipReportPanel } from '@/components/reports/TipReportPanel'
import { ExpiringPackagesReportPanel } from '@/components/reports/ExpiringPackagesReportPanel'
import { PackageRedemptionAuditReportPanel } from '@/components/reports/PackageRedemptionAuditReportPanel'
import { PackageSalesReportPanel } from '@/components/reports/PackageSalesReportPanel'
import { CrmReportPanel } from '@/components/reports/CrmReportPanel'
import { InventoryReportsPanel } from '@/components/reports/InventoryReportsPanel'
import { StylistPerformancePanel } from '@/components/reports/StylistPerformancePanel'
import { ReportsHub } from '@/components/reports/ReportsHub'
import { ReportPreferencesProvider, useReportPreferences } from '@/contexts/ReportPreferencesContext'

const REPORT_SECTIONS = [
  { key: 'branch-comparison', name: 'Branch comparison', category: 'financial', Panel: BranchComparisonReportPanel },
  { key: 'daily-sales', name: 'Daily sales summary', category: 'sales', Panel: DailySalesReportPanel },
  { key: 'payment-mix', name: 'Payment mix', category: 'sales', Panel: PaymentMixReportPanel },
  { key: 'service-mix', name: 'Service category mix', category: 'sales', Panel: ServiceMixReportPanel },
  { key: 'retail-sales', name: 'Retail sales', category: 'sales', Panel: RetailSalesReportPanel },
  { key: 'appointments', name: 'Appointments', category: 'operations', Panel: AppointmentReportPanel },
  { key: 'online-booking-funnel', name: 'Online booking funnel', category: 'operations', Panel: OnlineBookingFunnelReportPanel },
  { key: 'z-reports', name: 'Cash / Z-reports (historical)', category: 'accounts', Panel: ZReportHistoricalReportPanel },
  { key: 'vat-summary', name: 'VAT summary', category: 'financial', Panel: VatSummaryReportPanel },
  { key: 'gateway-transactions', name: 'Gateway transactions', category: 'financial', Panel: GatewayTransactionReportPanel },
  { key: 'void-discount-audit', name: 'Void & discount audit', category: 'sales', Panel: VoidDiscountAuditReportPanel },
  { key: 'stylist-performance', name: 'Stylist performance', category: 'staff', Panel: StylistPerformancePanel },
  { key: 'commissions', name: 'Stylist commissions', category: 'staff', Panel: CommissionReportPanel },
  { key: 'tips', name: 'Tips', category: 'staff', Panel: TipReportPanel },
  { key: 'package-sales', name: 'Package sales & liability', category: 'packages', Panel: PackageSalesReportPanel },
  { key: 'package-redemptions', name: 'Package redemption audit', category: 'packages', Panel: PackageRedemptionAuditReportPanel },
  { key: 'inventory', name: 'Inventory', category: 'inventory', Panel: InventoryReportsPanel },
  { key: 'crm', name: 'CRM', category: 'crm', Panel: CrmReportPanel },
  { key: 'expiring-packages', name: 'Expiring packages', category: 'packages', Panel: ExpiringPackagesReportPanel },
]

function InsightsPageContent() {
  const { activeReportKey, isReportVisible } = useReportPreferences()

  const activeSection = useMemo(() => {
    const visible = REPORT_SECTIONS.filter((section) =>
      isReportVisible(section.key, { name: section.name, category: section.category }),
    )

    return (
      visible.find((section) => section.key === activeReportKey)
      ?? visible[0]
      ?? null
    )
  }, [activeReportKey, isReportVisible])

  const ActivePanel = activeSection?.Panel

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <BarChart3 className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Insights</h1>
          <p className="text-sm text-muted-foreground">
            Pick a category and report — one view at a time, with remembered filters and favourites.
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] lg:items-start">
        <ReportsHub sections={REPORT_SECTIONS} />

        <div className="min-w-0">
          {ActivePanel ? (
            <ActivePanel key={activeSection.key} />
          ) : (
            <div className="card-surface flex min-h-[320px] items-center justify-center p-8 text-center text-sm text-muted-foreground">
              No report selected. Adjust search or category filters on the left.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function InsightsPage() {
  return (
    <ReportPreferencesProvider>
      <InsightsPageContent />
    </ReportPreferencesProvider>
  )
}
